import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function LeedsPage() {
  return (
    <>
      <LocalBusinessSchema city="Leeds" latitude={53.8008} longitude={-1.5491} />
      <PageHero
        title="Fractional CISO Services in Leeds"
        subtitle="Cybersecurity leadership for Yorkshire's digital powerhouse"
        eyebrow="Leeds"
        seoTitle="Fractional CISO Leeds | The CISO Network"
        seoDescription="Expert fractional CISO services for Leeds businesses. Access senior cybersecurity leadership for Yorkshire's fintech and legal tech sectors."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Yorkshire's Financial and Legal Tech Hub
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Leeds has established itself as the UK's second-largest financial centre and a
                  rapidly growing legal technology hub. The city combines a historic banking and
                  insurance heritage with a thriving fintech scene, creating a unique ecosystem
                  where traditional financial institutions and disruptive startups coexist and
                  collaborate.
                </p>
                <p>
                  Yorkshire businesses face demanding cybersecurity requirements. Financial services
                  firms must maintain rigorous security programmes to satisfy regulators and
                  clients. The city's burgeoning legaltech sector handles highly sensitive client
                  data requiring robust protection. Meanwhile, healthtech innovators working with
                  NHS organisations must navigate complex data governance frameworks.
                </p>
                <p>
                  Many Leeds businesses are scale-ups spun out of the city's universities or growing
                  SMEs serving enterprise clients. These businesses need demonstrable security
                  leadership to win contracts and investment, but often can't justify a full-time
                  CISO hire. Our fractional CISO service provides Leeds businesses with the
                  executive security expertise they need to compete on a national stage.
                </p>
                <p>
                  Leeds has emerged as the capital of the Northern Powerhouse, with a finance and
                  legal sector that rivals many European cities and a healthtech ecosystem growing
                  rapidly around the city's NHS trusts and universities. These sectors face
                  increasing regulatory scrutiny and customer security demands. A fractional CISO
                  helps Leeds businesses meet these expectations without the London price tag.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands Yorkshire's unique business environment, from
                      financial services to tech innovation.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Yorkshire Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Leeds, Bradford, Sheffield, and throughout
                      Yorkshire with local CISO expertise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Leeds Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Strategic security leadership for Yorkshire's digital economy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Financial Services Expertise',
                description:
                  'CISOs who understand FCA requirements and banking security standards.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  'Access senior expertise without the overhead of a full-time Yorkshire CISO.',
              },
              {
                icon: Users,
                title: 'Legal Tech Knowledge',
                description:
                  'Security leadership that understands the unique needs of legal technology firms.',
              },
              {
                icon: Clock,
                title: 'Scale-Up Ready',
                description:
                  'Security programmes that grow with your Leeds business and satisfy investors.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Leeds - FAQs"
        subtitle="Common questions from Leeds and Yorkshire businesses"
        items={[
          {
            id: 'leeds-1',
            question: 'How does a fractional CISO work with businesses in Leeds?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Leeds. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing finance, legal, and healthtech companies.',
          },
          {
            id: 'leeds-2',
            question: 'What size of business benefits from a fractional CISO in Leeds?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Leeds startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'leeds-3',
            question: 'What industries do your CISOs cover in Leeds?',
            answer:
              "Our network includes CISOs with experience in finance, legal, and healthtech. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'leeds-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'leeds-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Leeds Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your financial
            or legal tech business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
